import { useEffect, useRef, useState } from "react";

const useUserTalk = (isMicOn = true) => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const microphoneStreamRef = useRef<MediaStream | null>(null);
  const [isTalking, setIsTalking] = useState<boolean>(false);

  // Initialize audio analysis
  useEffect(() => {
    const initAudio = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        microphoneStreamRef.current = stream;

        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        analyzeAudio();
      } catch (error) {
        console.error('Error accessing microphone:', error);
      }
    };

    if (isMicOn) {
      initAudio();
    }

    return () => {
      if (microphoneStreamRef.current) {
        microphoneStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioContextRef.current && audioContextRef.current.state === 'running') {
        audioContextRef.current .close();
      }
    };
  }, [isMicOn]);

  const analyzeAudio = () => {
    const analyser = analyserRef.current;
    if (!analyser) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const checkVolume = () => {
      analyser.getByteFrequencyData(dataArray);

      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += dataArray[i];
      }
      const averageVolume = sum / bufferLength;

      const threshold = 20; // Adjust this value
      setIsTalking(averageVolume > threshold);

      requestAnimationFrame(checkVolume);
    };

    checkVolume();
  };

  return isTalking;
}

export default useUserTalk;
